import { useEffect, useRef } from "react";

// Note: we intentionally use console.error directly here (not devLog) because
// this is a low-level infrastructure hook and its errors indicate real bugs
// in polled callbacks — we want them visible in every environment.

/**
 * Calls `callback` every `intervalMs`. By default it only ticks while the tab
 * is visible (saves CPU / network on hidden tabs); pass `pollWhileHidden={true}`
 * to keep the timer running in the background — useful for cases where the
 * consumer wants to react (e.g., play a sound) even when the tab is blurred.
 * Also fires the callback immediately when the tab becomes visible after
 * being hidden, so users instantly see fresh data on tab-back.
 *
 * - callback: function to invoke. Latest reference is always used.
 * - intervalMs: poll interval in milliseconds.
 * - enabled: optional flag to pause polling.
 * - pollWhileHidden: keep ticking even on hidden tabs (default false).
 */
export function usePolling(callback, intervalMs, enabled = true, pollWhileHidden = false) {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;

    const tick = () => {
      if (!pollWhileHidden && document.visibilityState !== "visible") return;
      try {
        callbackRef.current?.();
      } catch (e) {
        console.error("[usePolling] tick failed", e);
      }
    };

    const id = setInterval(tick, intervalMs);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intervalMs, enabled, pollWhileHidden]);
}
